// @ts-strict-ignore
import * as React from 'react';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import List from 'antd/lib/list';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Tooltip from 'antd/lib/tooltip';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    PlusOutlined,
    WarningOutlined,
} from '@ant-design/icons';

import Text from 'antd/lib/typography/Text';
import * as API from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectAuthCredentials } from '../../selectors/selectors';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { globalMessage } from '../../utils/globalMessage';
import { BumpVersionFile, BumpVersionFileInput, TestBumpVersionPatternResult } from '../../types';
import { runwayColors } from '../styles/globalStyles';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface BumpVersionFilesModalProps extends AppComponentProps {
    open: boolean;
    initialFiles: BumpVersionFile[];
    onCancel: () => void;
}

const toInputs = (files: BumpVersionFile[]): BumpVersionFileInput[] =>
    (files ?? []).map((file) => ({ filename: file.fileName, customPattern: file.customPattern }));

// A pattern has to capture the version, otherwise resolution finds a match but no version to bump.
const validateCustomPattern = (pattern: string): string | null => {
    let regex: RegExp;
    try {
        regex = new RegExp(pattern);
    } catch {
        return 'Not a valid regular expression.';
    }
    // matching the empty alternation makes the groups array reflect the group count without a real match
    const captureGroupCount = new RegExp(`${regex.source}|`).exec('').length - 1;
    if (captureGroupCount < 1) {
        return 'Pattern has no capture group: wrap the version number in parentheses.';
    }
    return null;
};

const BumpVersionFilesModal: React.FC<BumpVersionFilesModalProps> = (props: BumpVersionFilesModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [files, setFiles] = React.useState<BumpVersionFileInput[]>(toInputs(props.initialFiles));
    const [testResults, setTestResults] = React.useState<Record<number, TestBumpVersionPatternResult | string>>({});
    const [testingIndex, setTestingIndex] = React.useState<number | null>(null);
    // rows whose pattern field is open while empty - a file with a pattern always shows it
    const [editingPatterns, setEditingPatterns] = React.useState<Record<number, boolean>>({});

    const editBumpFilesIsLoading = useIsLoading(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);
    const editBumpFilesError = useError(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);
    const editBumpFilesSuccess = useUpdated(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles);

    const [isGetVersionFilesLoading, setIsGetVersionFilesLoading] = React.useState(false);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const getDefaultBumpVersionFiles = async () => {
        setIsGetVersionFilesLoading(true);
        try {
            const fetchedDefaultFiles = await API.getDefaultBumpVersionFiles(
                { orgId: props.orgId, appId: props.appId },
                authCredentials,
            );
            setFiles(toInputs(fetchedDefaultFiles));
            setTestResults({});
            setEditingPatterns({});
        } catch (error) {
            globalMessage.error(`Error getting default files: ${error.message}`);
        }
        setIsGetVersionFilesLoading(false);
    };

    // cleared is the input's clear button, which drops the pattern and the field with it. Emptying the
    // field by hand keeps it open until it loses focus.
    const updatePattern = (index: number, customPattern: string, cleared: boolean) => {
        setFiles((prev) =>
            prev.map((file, i) => (i === index ? { ...file, customPattern: customPattern || undefined } : file)),
        );
        setTestResults((prev) => ({ ...prev, [index]: undefined }));
        setEditingPatterns((prev) => ({ ...prev, [index]: !cleared }));
    };

    const testFile = async (index: number) => {
        const file = files[index];
        if (file.customPattern) {
            const patternError = validateCustomPattern(file.customPattern);
            if (patternError) {
                setTestResults((prev) => ({ ...prev, [index]: patternError }));
                return;
            }
        }

        setTestingIndex(index);
        try {
            const result = await API.testBumpVersionPattern(
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    filename: file.filename,
                    customPattern: file.customPattern,
                },
                authCredentials,
            );
            setTestResults((prev) => ({ ...prev, [index]: result }));
        } catch (error) {
            setTestResults((prev) => ({ ...prev, [index]: error.message }));
        }
        setTestingIndex(null);
    };

    const saveBumpVersionFiles = () => {
        for (const file of files) {
            if (!file.customPattern) {
                continue;
            }
            const patternError = validateCustomPattern(file.customPattern);
            if (patternError) {
                globalMessage.error(`${file.filename}: ${patternError}`);
                return;
            }
        }

        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateBumpVersionOverrideFiles, {
                orgId: props.orgId,
                appId: props.appId,
                files,
            }),
        );
    };

    React.useEffect(() => {
        if (editBumpFilesError) {
            globalMessage.error(editBumpFilesError);
        }
    }, [editBumpFilesError]);

    React.useEffect(() => {
        if (editBumpFilesSuccess) {
            globalMessage.success('Bump version files updated!');

            props.onCancel();
        }
    }, [editBumpFilesSuccess]);

    // one line, whatever the message - a wrapping result would resize the row it belongs to
    const renderTestResult = (result: TestBumpVersionPatternResult | string) => {
        if (!result) {
            return null;
        }

        let type: 'danger' | 'warning' | 'success' = 'danger';
        let icon = <CloseCircleOutlined />;
        let message: string;
        let detail: string | undefined;

        if (typeof result === 'string') {
            message = result;
        } else if (!result.version) {
            message = result.errMsg ?? 'No valid version found in file';
            detail = result.errDetail;
        } else {
            const matchedMoreThanOnce = result.matchCount > 1;
            type = matchedMoreThanOnce ? 'warning' : 'success';
            icon = matchedMoreThanOnce ? <WarningOutlined /> : <CheckCircleOutlined />;
            message =
                `Found ${result.version}` +
                (result.matchCount === undefined ?
                    ''
                :   ` · ${result.matchCount} ${result.matchCount === 1 ? 'match' : 'matches'}`);
        }

        return (
            <Tooltip title={detail ? `${message} ${detail}` : message}>
                <Text type={type} style={{ fontSize: 12 }}>
                    {icon} {message}
                </Text>
            </Tooltip>
        );
    };

    return (
        <Modal
            title="Edit bump version files"
            footer={
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        type="link"
                        loading={isGetVersionFilesLoading}
                        onClick={getDefaultBumpVersionFiles}
                        disabled={editBumpFilesIsLoading}
                    >
                        Find existing version files
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Button onClick={props.onCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={editBumpFilesIsLoading}
                        onClick={saveBumpVersionFiles}
                        disabled={isGetVersionFilesLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                setFiles(toInputs(props.initialFiles));
                setTestResults({});
                setEditingPatterns({});
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                    Specify the file path(s) for your app&apos;s version files. Runway will bump the version in these
                    files during manual or automated version bumps. Custom patterns are supported.&nbsp;
                    <TooltipInfoIcon
                        tooltipContent={
                            <>
                                Add a custom pattern if the version can&apos;t be detected automatically. A pattern is a
                                regular expression whose first capture group is the version, for example:
                                <div style={{ marginTop: 4 }}>
                                    <Text code style={{ color: runwayColors.white }}>
                                        version: &quot;(\d+\.\d+\.\d+)&quot;
                                    </Text>
                                </div>
                            </>
                        }
                    />
                </Text>

                <Form
                    form={form}
                    style={{
                        display: 'flex',
                        marginTop: 24,
                    }}
                    onFinish={(values) => {
                        const newFilename = values.filename;
                        if (!newFilename) {
                            return;
                        }
                        if (files.some((file) => file.filename === newFilename)) {
                            globalMessage.error(`${newFilename} is already on the list.`);
                            return;
                        }
                        setFiles((prev) => [...prev, { filename: newFilename }]);
                        form.resetFields();
                    }}
                >
                    <Form.Item
                        style={{ marginRight: 12, flex: 1 }}
                        name="filename"
                        rules={[{ required: true, message: 'Please enter a file path.' }]}
                    >
                        <Input placeholder={`path/some_file.extension`} autoFocus allowClear />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Form>

                <List
                    style={{ backgroundColor: runwayColors.white }}
                    bordered
                    size="small"
                    locale={{ emptyText: 'No files added yet' }}
                    dataSource={files}
                    renderItem={(file, index) => (
                        <List.Item key={index} style={{ paddingTop: 10, paddingBottom: 10 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                    <div
                                        style={{
                                            flex: 1,
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {file.filename}
                                    </div>
                                    <Tooltip title="Resolve the version from this file on your working branch">
                                        <Button
                                            size="small"
                                            type="link"
                                            style={{ fontSize: 11, letterSpacing: 0.8, textTransform: 'uppercase' }}
                                            loading={testingIndex === index}
                                            onClick={() => testFile(index)}
                                        >
                                            Test
                                        </Button>
                                    </Tooltip>
                                    <Button
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        type="link"
                                        style={{ color: runwayColors.statusGrey }}
                                        onClick={() => {
                                            setFiles((prev) => prev.filter((_, i) => i !== index));
                                            setTestResults({});
                                            setEditingPatterns({});
                                        }}
                                    />
                                </div>
                                <div style={{ marginTop: 8, height: 24 }}>
                                    {file.customPattern !== undefined || editingPatterns[index] ?
                                        <Input
                                            style={{ height: 24 }}
                                            size="small"
                                            placeholder={`version: "(\\d+\\.\\d+\\.\\d+)"`}
                                            value={file.customPattern ?? ''}
                                            onChange={(e) => updatePattern(index, e.target.value, e.type === 'click')}
                                            onBlur={() => {
                                                if (!file.customPattern) {
                                                    setEditingPatterns((prev) => ({ ...prev, [index]: false }));
                                                }
                                            }}
                                            autoFocus={editingPatterns[index]}
                                            allowClear
                                        />
                                    :   <Button
                                            type="link"
                                            size="small"
                                            style={{ padding: 0, height: 24, alignSelf: 'flex-start' }}
                                            icon={<PlusOutlined />}
                                            onClick={() => setEditingPatterns((prev) => ({ ...prev, [index]: true }))}
                                        >
                                            Add custom pattern
                                        </Button>
                                    }
                                </div>
                                {/* fixed slot, empty until a result is in, so testing a row doesn't shift the list */}
                                <div style={{ marginTop: 6, height: 20, lineHeight: '20px' }}>
                                    {renderTestResult(testResults[index])}
                                </div>
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </Modal>
    );
};

export default BumpVersionFilesModal;
